/**
 * @fileoverview Component for displaying a recover password form.
 * This component includes a form for recovering a password and displays a message if the password is incorrect or duplicated.
 */

import { ArrowRightIcon, EnvelopeIcon } from '@heroicons/react/20/solid'
import { AxiosError } from 'axios'
import { useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import NovoicDashboardLogo from '../../assets/novoic-dashboard-logo.svg'
import { useCheckLinkValidityQuery, useRecoverPasswordMutation } from '../../services/users'
import { RecoverPasswordForm } from './RecoverPasswordForm'

/**
 * @interface Payload
 * @description The payload for the recover password mutation.
 * @property {string} password - The new password.
 * @property {string} token - The token for the password recovery.
 */
type Payload = {
    password: string
    token: string
}

/**
 * @function useQuery
 * @description Hook for parsing the query parameters from the URL.
 * @returns {URLSearchParams} - The query parameters.
 */
function useQuery() {
    const { search } = useLocation()

    return useMemo(() => new URLSearchParams(search), [search])
}

/**
 * @function RecoverPassword
 * @description Component for displaying a recover password form.
 * @returns {JSX.Element} - A React component that renders the recover password form.
 */
export default function RecoverPassword() {
    const query = useQuery()
    const navigate = useNavigate()
    const token = query.get('token') as string

    const [isPending, setIsPending] = useState(false)
    const [recoverPassword, { isSuccess }] = useRecoverPasswordMutation()
    const {
        isSuccess: linkIsValid,
        isFetching: isCheckingValidity,
        isLoading,
    } = useCheckLinkValidityQuery({
        token,
    })
    const [isWrongPassword, setIsWrongPassword] = useState(false)
    const [isDuplicatedPassword, setIsDuplicatedPassword] = useState(false)

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsPending(true)

        const formData = new FormData(e.currentTarget)
        const payload: Payload = {
            password: formData.get('password') as string,
            token,
        }

        try {
            await recoverPassword(payload).unwrap()
        } catch (e: unknown) {
            if ((e as AxiosError).status === 400) {
                setIsWrongPassword(true)
            } else if ((e as AxiosError).status === 409) {
                setIsDuplicatedPassword(true)
            } else {
                console.error(e)
            }
        } finally {
            setIsPending(false)
        }
    }

    if (isCheckingValidity) {
        return null
    }

    if (!isLoading && !linkIsValid) {
        navigate('/password/expired')
    }

    return (
        <div className="nv-grid min-h-screen">
            <div className="lg-container max-h-full items-center content-center justify-center col-start-1 md:col-start-2 lg:col-start-4">
                <div className="w-full max-w-md space-y-8">
                    <div className="flex flex-col items-center gap-y-4">
                        <NovoicDashboardLogo className="w-56" />
                        <h2 className="text-center text-3xl font-bold font-serif text-gray-900">
                            Create new password
                        </h2>

                        <p className="text-gray-600 font-regular text-center">
                            Please create a unique new password that you have not previously used.
                        </p>
                    </div>

                    {!isSuccess ? (
                        <RecoverPasswordForm
                            onSubmit={onSubmit}
                            isWrongPassword={isWrongPassword}
                            isDuplicatedPassowrd={isDuplicatedPassword}
                            isPending={isPending}
                        />
                    ) : (
                        <>
                            <div className="rounded-md bg-indigo-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <EnvelopeIcon
                                            className="h-5 w-5 text-indigo-400"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="ml-3 flex-1 md:flex md:justify-between">
                                        <p className="text-sm text-indigo-800 font-medium">
                                            New password succesfully created. Please use it for
                                            logging in to your account from now on.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-center items-center">
                                <NavLink
                                    to="/login"
                                    className="inline-flex gap-x-1 px-2 py-1 rounded-md transition-all text-indigo-600 hover:bg-indigo-50"
                                >
                                    <span className="font-medium text-sm">
                                        Log In to Your Account
                                    </span>
                                    <ArrowRightIcon className="w-5 h-5 stroke-indigo-600" />
                                </NavLink>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
